import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import { getCurrentUser } from 'aws-amplify/auth';
import './AuthComponent.css';

const AuthComponent = ({ setUser }) => {
  const [authState, setAuthState] = useState('signIn');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    confirmationCode: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const user = await getCurrentUser();
      setUser(user);
      navigate('/');
    } catch (error) {
      console.log('No authenticated user:', error);
    }
  }

  function onChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  function isPasswordStrong(password) {
    const strongPasswordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()])[A-Za-z\d!@#$%^&*()]{8,}$/;
    return strongPasswordRegex.test(password);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!isPasswordStrong(formData.password)) {
      setError("Password must be at least 8 characters long and include a number, an uppercase letter, and a special character");
      return;
    }
    
    setLoading(true);
    try {
      const { username, password, email } = formData;
      await signUp({
        username: username.toLowerCase(), // Convert username to lowercase
        password,
        options: {
          userAttributes: {
            email
          },
          autoSignIn: { enabled: true }
        }
      });
      setAuthState('confirmSignUp');
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmSignUp(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({
        username: formData.username.toLowerCase(), // Convert username to lowercase
        confirmationCode: formData.confirmationCode
      });
      setAuthState('signIn');
    } catch (error) {
      console.error('Error confirming sign up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { username, password } = formData;
      await signIn({ username: username.toLowerCase(), password }); // Convert username to lowercase
      const user = await getCurrentUser();
      setUser(user);
      navigate('/');
    } catch (error) {
      console.error('Error signing in:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-component">
      {authState === 'signUp' && (
        <form onSubmit={handleSignUp} className="auth-form">
          <h2>Sign Up</h2>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input id="username" name="username" onChange={onChange} placeholder="Username" required />
            <p className="form-hint">Usernames are not case-sensitive.</p>
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input id="email" name="email" type="email" onChange={onChange} placeholder="Email" required />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input 
              id="password"
              name="password" 
              type="password" 
              onChange={onChange} 
              placeholder="Password" 
              required 
            />
            <p className="form-hint">Password must be at least 8 characters long and include a number, an uppercase letter, and a special character.</p>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input 
              id="confirmPassword"
              name="confirmPassword" 
              type="password" 
              onChange={onChange} 
              placeholder="Confirm Password" 
              required 
            />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Signing Up...' : 'Sign Up'}
          </button>
          <p>
            Already have an account? <button type="button" onClick={() => setAuthState('signIn')}>Sign In</button>
          </p>
        </form>
      )}
      {authState === 'confirmSignUp' && (
        <form onSubmit={handleConfirmSignUp} className="auth-form">
          <h2>Confirm Sign Up</h2>
          <div className="form-group">
            <label htmlFor="confirmationCode">Confirmation Code</label>
            <input id="confirmationCode" name="confirmationCode" onChange={onChange} placeholder="Confirmation Code" required />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Confirming...' : 'Confirm Sign Up'}
          </button>
        </form>
      )}
      {authState === 'signIn' && (
        <form onSubmit={handleSignIn} className="auth-form">
          <h2>Sign In</h2>
          <div className="form-group">
            <label htmlFor="signinUsername">Username</label>
            <input id="signinUsername" name="username" onChange={onChange} placeholder="Username" required />
            <p className="form-hint">Usernames are not case-sensitive.</p>
          </div>
          <div className="form-group">
            <label htmlFor="signinPassword">Password</label>
            <input id="signinPassword" name="password" type="password" onChange={onChange} placeholder="Password" required />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
          <p>
            Don't have an account? <button type="button" onClick={() => setAuthState('signUp')}>Sign Up</button>
          </p>
        </form>
      )}
      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default AuthComponent;